import { useMemo, useState } from 'react'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    LabelList,
} from 'recharts'

import { Card, CardContent } from '@/components/ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { Checkbox } from '@/components/ui/checkbox'
import MyCheckbox from '@/components/zthd/MyCheckbox'

const chartConfig = {
    dnu: {
        label: 'DNU',
        color: 'hsl(var(--chart-1))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_trial_started: {
        label: '开始试用',
        color: 'hsl(var(--chart-2))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_trial_canceled: {
        label: '取消试用',
        color: 'hsl(var(--chart-3))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_trial_churned: {
        label: '试用后流失',
        color: 'hsl(var(--chart-4))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_trial_converted: {
        label: '试用后转化',
        color: 'hsl(var(--chart-5))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_started: {
        label: '套餐生效',
        color: 'hsl(var(--chart-6))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_canceled: {
        label: '订阅已取消',
        color: 'hsl(var(--chart-7))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_paused: {
        label: '订阅已暂停(Android)',
        color: 'hsl(var(--chart-8))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_resumed: {
        label: '订阅已恢复',
        color: 'hsl(var(--chart-9))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_churned: {
        label: '订阅用户流失',
        color: 'hsl(var(--chart-10))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_refunded: {
        label: '订阅已退款',
        color: 'hsl(var(--chart-11))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_billing_grace: {
        label: '订阅计费宽限期',
        color: 'hsl(var(--chart-12))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_renewed: {
        label: '订阅已更新',
        color: 'hsl(var(--chart-13))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_subscription_xgraded: {
        label: '订阅已变更',
        color: 'hsl(var(--chart-14))',
        formatter: (v) => v,
        yAxis: 'left',
    },
    af_ars_existing_subscriber: {
        label: '现有订阅用户',
        color: 'hsl(var(--chart-15))',
        formatter: (v) => v,
        yAxis: 'left',
    },
}

const metricGroups = {
    dnu: {
        label: 'DNU',
        metrics: ['dnu'],
    },
    trial: {
        label: 'Trial',
        metrics: [
            'af_ars_trial_started',
            'af_ars_trial_canceled',
            'af_ars_trial_churned',
            'af_ars_trial_converted',
        ],
    },
    subscription: {
        label: 'Subscription',
        metrics: [
            'af_ars_subscription_started',
            'af_ars_subscription_canceled',
            'af_ars_subscription_paused',
            'af_ars_subscription_resumed',
            'af_ars_subscription_churned',
            'af_ars_subscription_billing_grace',
            'af_ars_subscription_refunded',
            'af_ars_subscription_renewed',
            'af_ars_subscription_xgraded',
            'af_ars_existing_subscriber',
        ],
    },
}

const SubscribeChart = ({ data }) => {
    const [selectedMetrics, setSelectedMetrics] = useState([
        'dnu',
        'af_ars_trial_started',
        'af_ars_subscription_started',
    ])
    const [showLabel, setShowLabel] = useState(false)

    const processedData = useMemo(() => {
        return (data || []).map((item) => {
            const base = { ...item, date: item.eventAt?.slice(5) }
            Object.keys(chartConfig).forEach((metricKey) => {
                if (metricKey === 'dnu') {
                    base[metricKey] = item.dnu
                } else {
                    if (item[metricKey] && typeof item[metricKey] === 'object' && 'userCount' in item[metricKey]) {
                        base[metricKey] = item[metricKey].userCount
                    } else {
                        base[metricKey] = 0
                    }
                }
            })
            return base
        })
    }, [data])

    return (
        <Card className="shadow-none border-none">
            <div className="flex flex-wrap gap-4 px-6">
                {Object.entries(metricGroups).map(([groupKey, group]) => (
                    <div key={groupKey} className="space-y-2">
                        <h3 className="text-sm font-semibold">{group.label}</h3>
                        <div className="flex flex-wrap gap-4">
                            {group.metrics.map((metricKey) => {
                                const metric = chartConfig[metricKey]
                                return (
                                    <div key={metricKey} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={metricKey}
                                            checked={selectedMetrics.includes(metricKey)}
                                            onCheckedChange={(checked) => {
                                                setSelectedMetrics((prev) =>
                                                    checked
                                                        ? [...prev, metricKey]
                                                        : prev.filter((m) => m !== metricKey),
                                                )
                                            }}
                                        />
                                        <label
                                            htmlFor={metricKey}
                                            className="text-sm font-medium"
                                            style={{ color: metric.color }}
                                        >
                                            {metric.label}
                                        </label>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex items-center px-6">
                <MyCheckbox
                    label="Show Label"
                    checked={showLabel}
                    onCheckedChange={setShowLabel}
                />
            </div>
            <CardContent>
                <ChartContainer
                    className="aspect-auto h-[500px] w-full"
                    config={chartConfig}
                >
                    <LineChart
                        data={processedData}
                        margin={{ top: 20, right: 50, bottom: 20, left: 50 }}
                    >
                        <CartesianGrid vertical={false} />
                        <XAxis dataKey="date" tickLine={false} axisLine={false} />
                        <YAxis
                            yAxisId="left"
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value) => value}
                        />
                        <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent indicator="dot" />}
                        />
                        {selectedMetrics.map((metricKey) => {
                            const metric = chartConfig[metricKey]
                            return (
                                <Line
                                    key={metricKey}
                                    type="monotone"
                                    dataKey={metricKey}
                                    stroke={metric.color}
                                    yAxisId={metric.yAxis}
                                    strokeWidth={2}
                                    dot={{ fill: metric.color }}
                                    activeDot={{ r: 6 }}
                                >
                                    {showLabel && (
                                        <LabelList
                                            position="top"
                                            offset={12}
                                            formatter={metric.formatter}
                                        />
                                    )}
                                </Line>
                            )
                        })}
                    </LineChart>
                </ChartContainer>
            </CardContent>
        </Card>
    )
}

export default SubscribeChart
